import type { Category, ChoiceGroup, Context, MenuItem, Order, Receipt } from "@/lib/api/types";

/**
 * First-slice fixtures — the restaurant order->checkout state from
 * docs/design/pos-mocks/1-ledger-ink.html, shaped exactly as pos-orchestrator would
 * return it (server-authoritative totals). Used in mock mode so pos-webapp renders and is
 * reviewable before the BFF exists. Amounts carry integer `minor` (paise) + server `display`.
 */

// Fixture-only INR authoring helper (Indian grouping). NOT used for runtime arithmetic —
// the app renders Money.display verbatim (see lib/format/money.ts).
function inr(rupees: number, opts: { debit?: boolean } = {}) {
  const minor = Math.round(rupees * 100);
  const whole = Math.trunc(Math.abs(rupees));
  const paise = String(Math.round((Math.abs(rupees) - whole) * 100)).padStart(2, "0");
  const grouped = whole
    .toString()
    .replace(/\B(?=(\d{2})+(\d{3})(?!\d))/g, ","); // Indian lakh grouping
  const sign = opts.debit ? "−" : "";
  return { currency: "INR", minor, display: `${sign}₹${grouped}.${paise}` };
}

export const contextFixture: Context = {
  staff: { id: "u-priya", name: "Priya", role: "CASHIER", discountLimitPct: 10 },
  location: { id: "BLR-01", name: "Indiranagar", gstin: "29ABCDE1234F1Z5", stateCode: "29" },
  currency: "INR",
  locale: "en-IN",
  taxProfile: "IN_GST",
  packs: ["restaurant"],
  vocabulary: { resource: "table", bill: "order" },
  screens: ["orders", "floor", "menu", "customers", "shifts", "reports", "settings"],
};

/**
 * Mock-only staff variants used by browser coverage. Production identity still comes from the BFF.
 * Keeping the role switch in the fixture makes each Playwright test independent without teaching
 * the UI about test credentials or introducing a fake login endpoint.
 */
export function contextFixtureForMockRole(role: string | null, pack?: string | null): Context {
  const staffByRole: Record<string, Context["staff"]> = {
    cashier: { id: "u-priya", name: "Priya", role: "CASHIER", discountLimitPct: 10 },
    manager: { id: "u-maya", name: "Maya", role: "TENANT_MANAGER", discountLimitPct: 25 },
    owner: { id: "u-aarav", name: "Aarav", role: "TENANT_ADMIN", discountLimitPct: 100 },
    support: { id: "u-support", name: "Support Desk", role: "TENANT_SUPPORT_AGENT", discountLimitPct: 0 },
  };

  const staff = role ? staffByRole[role] : undefined;
  const base = staff ? { ...contextFixture, staff } : contextFixture;
  // A Reward Desk counter: the mock build is a restaurant unless a test or a reviewer asks for the desk.
  // The desk is Kaveri Sweets' Jayanagar counter: Mina runs it, Divya is on the till.
  const deskStaff: Record<string, Context["staff"]> = {
    cashier: { id: "u-divya", name: "Divya Nair", role: "CASHIER", discountLimitPct: 10 },
    manager: { id: "u-mina", name: "Mina Menon", role: "TENANT_MANAGER", discountLimitPct: 25 },
    owner: { id: "u-mina", name: "Mina Menon", role: "TENANT_ADMIN", discountLimitPct: 100 },
  };
  return pack === "desk"
    ? {
        ...base,
        staff: (role ? deskStaff[role] : undefined) ?? base.staff,
        location: { ...contextFixture.location, id: "BLR-02", name: "Jayanagar" },
        packs: ["desk"],
        screens: [],
        vocabulary: { customer: "Customer", bill: "Bill" },
        entitlements: [{ capability: "desk.rewards", enabled: true }],
      }
    : base;
}

export const categoriesFixture: Category[] = [
  { id: "all", name: "All" },
  { id: "starters", name: "Starters" },
  { id: "tandoor", name: "Tandoor" },
  { id: "mains", name: "Mains" },
  { id: "biryani", name: "Biryani & Rice" },
  { id: "breads", name: "Breads" },
  { id: "desserts", name: "Desserts" },
  { id: "beverages", name: "Beverages" },
];

export const menuFixture: MenuItem[] = [
  { id: "paneer-tikka", name: "Paneer Tikka", category: "tandoor", price: inr(280), veg: true, bestseller: true, stock: "IN_STOCK" },
  { id: "butter-chicken", name: "Butter Chicken", category: "mains", price: inr(380), veg: false, bestseller: true, stock: "IN_STOCK", modifiers: [{ id: "extra-butter", name: "Extra butter", price: inr(30) }] },
  { id: "dal-makhani", name: "Dal Makhani", category: "mains", price: inr(260), veg: true, stock: "IN_STOCK" },
  { id: "chicken-biryani", name: "Chicken Biryani", category: "biryani", price: inr(340), veg: false, stock: "IN_STOCK" },
  { id: "garlic-naan", name: "Garlic Naan", category: "breads", price: inr(70), veg: true, stock: "IN_STOCK" },
  { id: "tandoori-roti", name: "Tandoori Roti", category: "breads", price: inr(40), veg: true, stock: "IN_STOCK" },
  { id: "masala-chai", name: "Masala Chai", category: "beverages", price: inr(60), veg: true, stock: "IN_STOCK" },
  { id: "gulab-jamun", name: "Gulab Jamun", category: "desserts", price: inr(120), veg: true, stock: "IN_STOCK" },
  { id: "veg-seekh", name: "Veg Seekh Kebab", category: "starters", price: inr(240), veg: true, stock: "LOW" },
  { id: "rogan-josh", name: "Mutton Rogan Josh", category: "mains", price: inr(420), veg: false, stock: "OUT_OF_STOCK" },
  { id: "jeera-rice", name: "Jeera Rice", category: "biryani", price: inr(180), veg: true, stock: "IN_STOCK" },
  { id: "lime-soda", name: "Fresh Lime Soda", category: "beverages", price: inr(90), veg: true, stock: "IN_STOCK" },
];

/** The questions asked on the dishes that have any, by item id; every other dish is sold with none. */
export const choiceGroupsFixture: Record<string, ChoiceGroup[]> = {
  "butter-chicken": [
    {
      groupId: "g-spice",
      name: "Spice",
      kind: "ONE",
      mustPick: true,
      choices: [
        { choiceId: "mild", label: "Mild", priceChange: inr(0) },
        { choiceId: "hot", label: "Hot", priceChange: inr(0) },
      ],
    },
    {
      groupId: "g-extras",
      name: "Extras",
      kind: "UP_TO",
      maxPicks: 2,
      mustPick: false,
      choices: [
        { choiceId: "extra-butter", label: "Extra butter", priceChange: inr(30) },
        { choiceId: "less-oil", label: "Less oil", priceChange: inr(-10) },
      ],
    },
    { groupId: "g-note", name: "Special request", kind: "WORDS", mustPick: false, choices: [] },
  ],
};

export const orderFixture: Order = {
  id: "ord-T07-2207",
  tableId: "T-07",
  orderType: "DINE_IN",
  guests: 4,
  state: "AWAITING_PAYMENT",
  customer: { id: "c-ananya", name: "Ananya", phoneMasked: "+91 98•••• ••41", loyaltyPoints: 1240 },
  lines: [
    { id: "l1", itemId: "butter-chicken", name: "Butter Chicken", qty: 1, modifiers: [{ name: "Extra butter", price: inr(30) }], lineTotal: inr(410), kotStatus: "PREPARING" },
    { id: "l2", itemId: "garlic-naan", name: "Garlic Naan", qty: 4, lineTotal: inr(280), kotStatus: "PREPARING" },
    { id: "l3", itemId: "paneer-tikka", name: "Paneer Tikka", qty: 1, note: "less spicy", lineTotal: inr(280), kotStatus: "SENT" },
    { id: "l4", itemId: "masala-chai", name: "Masala Chai", qty: 2, lineTotal: inr(120), kotStatus: "NEW" },
  ],
  tenders: [
    { id: "t1", method: "POINTS", amount: inr(100), status: "APPLIED", note: "1,240 pts available · applied ₹100" },
  ],
  totals: {
    subtotal: inr(1090),
    discounts: [{ code: "WELCOME10", label: "Promo WELCOME10", amount: inr(109, { debit: true }) }],
    taxes: [
      // `rate`, not `ratePct` — the kernel's own field name; see TaxLine in lib/api/types.ts.
      { label: "CGST 2.5%", rate: 2.5, amount: inr(24.53), exempt: false },
      { label: "SGST 2.5%", rate: 2.5, amount: inr(24.53), exempt: false },
    ],
    roundOff: inr(0.06, { debit: true }),
    grandTotal: inr(1030),
    remaining: inr(930),
  },
  earnPreview: { points: 93, message: "Ananya earns 93 pts on this bill" },
  updatedAt: "2026-07-24T20:14:00+05:30",
};

export const receiptFixture: Receipt = {
  id: "rcpt-0417",
  orderId: orderFixture.id,
  invoiceNo: "SRH/BLR/2026-27/00417",
  paid: inr(930),
  pointsEarned: 93,
  issuedAt: "2026-07-24T20:16:00+05:30",
  deliveryOptions: ["WHATSAPP", "PRINT", "THERMAL"],
};
